package gr.tech.site.pages.landings

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Background
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.functions.LinearGradient
import com.varabyte.kobweb.compose.css.functions.linearGradient
import com.varabyte.kobweb.compose.css.functions.toImage
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.pages.SitePage
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initOurServicesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Our Services — LED, HVAC, Dehumidifier Rebates & Financing",
        description = "Green Rebates finds every available energy rebate and incentive for commercial growers — LED lighting, HVAC, dehumidification, and financing support. No account required.",
        canonicalUrl = "https://greenrebates.com/our-services",
        ogImage = "/images/ledgrowlights2.jpg"
    ))
}

@Page(SitePage.Landing.OUR_SERVICES)
@Layout(".components.layouts.PageLayout")
@Composable
fun EnergyRebatesIncentivesPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        ) {
            Img(
                src = ImageRepo.LEDGrowLights2.src,
                alt = ImageRepo.LEDGrowLights2.alt,
                attrs = Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Cover)
                    .toAttrs { attr("fetchpriority", "high") }
            )
            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .background(
                        Background.of(
                            image = linearGradient(
                                from = Theme.DarkGreen.rgb,
                                to = rgba(2, 67, 66, 0),
                                dir = LinearGradient.Direction.ToRight
                            ).toImage()
                        )
                    )
                    .toAttrs()
            )
            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            ) {
                H3(attrs = LandingHeroEyebrowStyle.toModifier().classNames("text-uppercase", "mb-2").toAttrs()) {
                    Text("Energy Rebates & Incentives for Commercial Growers")
                }
                H1(attrs = LandingHeroHeadlineStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Get Paid to Upgrade Your Facility")
                }
                P(attrs = LandingHeroLeadStyle.toModifier().classNames("mb-4").toAttrs()) {
                    Text("Green Rebates finds every available energy incentive for your indoor grow, cultivation facility, or greenhouse, including LED lighting, HVAC, dehumidification, and financing support. No accounts, no portals, no extra steps. Just faster rebates with a team that handles everything for you.")
                }
                Link(
                    path = "/contact-us",
                    text = "See What You Qualify For \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaPrimaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "See what rebates and incentives your facility qualifies for") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // SERVICES GRID
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-2").toAttrs()) {
                    Text("What We Do")
                }
                P(attrs = LandingSectionSubtitleStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Full-service rebate and incentive management for every major equipment category in your grow operation.")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "row-cols-lg-4", "g-4").toAttrs()) {
                    listOf(
                        Triple(SitePage.Landing.LED_GROW_LIGHT_REBATES, "LED Lighting Rebates",
                            "Recover up to 100% of the cost of DLC-qualified LED grow lights through utility rebate programs. The single largest rebate opportunity for most indoor grow facilities."),
                        Triple(SitePage.Landing.DEHUMIDIFIER_REBATES, "Dehumidifier Rebates",
                            "Commercial dehumidification systems qualify for significant energy incentive programs. Green Rebates identifies every applicable program for your cultivation facility."),
                        Triple(SitePage.Landing.HVAC_REBATES, "HVAC Rebates",
                            "HVAC is one of the highest energy draws in any grow operation. Utility HVAC rebate programs can offset a major share of your upgrade or replacement costs."),
                        Triple(SitePage.Landing.FINANCING, "Financing Support",
                            "Don't let upfront costs delay your equipment upgrade. We connect commercial growers with financing options that work alongside your pending rebates.")
                    ).forEachIndexed { index, (path, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Link(
                                path = path,
                                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                                modifier = LandingRebateCardStyle.toModifier()
                                    .classNames("d-flex", "flex-column", "h-100")
                            ) {
                                when (index) {
                                    0 -> FaLightbulb(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    1 -> FaDroplet(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    2 -> FaFan(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                    else -> FaSeedling(modifier = Modifier.classNames("mb-3"), size = IconSize.X2)
                                }
                                H5(attrs = LandingRebateCardTitleStyle.toModifier().classNames("mb-2").toAttrs()) {
                                    Text(title)
                                }
                                P(attrs = LandingRebateCardBodyStyle.toModifier().classNames("mb-3", "flex-grow-1").toAttrs()) {
                                    Text(desc)
                                }
                                Span(attrs = LandingRebateCardBodyStyle.toModifier().classNames("fw-semibold").toAttrs()) {
                                    Text("Learn More \u2192")
                                }
                            }
                        }
                    }
                }

                Div(attrs = Modifier.classNames("text-center", "mt-5").toAttrs()) {
                    Link(
                        path = "/contact-us",
                        text = "Not sure where to start? Contact us \u2192",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = LandingCtaSecondaryStyle.toModifier()
                            .role("button")
                            .attrsModifier { attr("aria-label", "Contact Green Rebates to find out which services apply to your facility") },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
            }
        }

        // HOW IT WORKS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.LightGray.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingSectionHeadingStyle.toModifier().classNames("mb-2").toAttrs()) {
                    Text("How Green Rebates Works")
                }
                P(attrs = LandingSectionSubtitleStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Three steps. We handle the rest.")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4").toAttrs()) {
                    listOf(
                        Triple("1", "Tell Us About Your Facility",
                            "No account needed. Just share the basics about your equipment, utility, and location. We take it from there."),
                        Triple("2", "We Find Every Available Incentive",
                            "Our team researches every applicable LED, HVAC, dehumidifier, and grow operation rebate program available through your utility — and handles every application."),
                        Triple("3", "You Collect Your Check",
                            "Once approved, your rebate arrives. No chasing utilities, no follow-up paperwork — just money back on upgrades you were already planning.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3").toAttrs()) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(attrs = LandingStepCardTitleStyle.toModifier().classNames("mb-1").toAttrs()) { Text(title) }
                                P(attrs = LandingStepCardBodyStyle.toModifier().classNames("mb-0").toAttrs()) { Text(desc) }
                            }
                        }
                    }
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(attrs = LandingDarkSectionHeadingStyle.toModifier().classNames("mb-5").toAttrs()) {
                    Text("Why Commercial Growers Choose Green Rebates")
                }
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4").toAttrs()) {
                    listOf(
                        "We Specialize in Grow Operations" to
                            "We work exclusively with commercial growers, cannabis cultivators, and horticulture facilities — not general energy consulting. We know which programs apply to your operation.",
                        "No Account, No Runaround" to
                            "You don't need to create an account, sign up for a portal, or wait for a callback. We get to work the moment you reach out.",
                        "Full-Service From Start to Check" to
                            "From initial audit to final payout, we manage every application, every form, and every utility follow-up — for LED, HVAC, dehumidifier, and more.",
                        "We Maximize Every Dollar" to
                            "We research every available incentive program — including stacking opportunities — to ensure your facility captures the maximum possible rebate funding."
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) { Text(title) }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) { Text(desc) }
                                }
                            }
                        }
                    }
                }
            }
        }

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(attrs = LandingCtaHeadingStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Find Out What Your Facility Qualifies For")
                }
                P(attrs = LandingCtaBodyStyle.toModifier().classNames("mb-4", "mx-auto").toAttrs()) {
                    Text("No account. No signup. No obligation. Green Rebates maximizes energy rebates and incentives for commercial growers nationwide — LED lighting, HVAC, dehumidification, and more. Get started today.")
                }
                Link(
                    path = "/contact-us",
                    text = "Get a Free Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free energy rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        RelatedSection(listOf(
            "/article/top-10-high-efficiency-led-rebates-for-commercial-cultivators-in-2026" to "Top 10 High-Efficiency LED Rebates for Commercial Cultivators in 2026",
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/blog/green-rebates-cannabis-business-times-2026-best-cannabis-companies-to-work" to "We Made The List! | Cannabis Business Times 2026 Best Cannabis Companies To Work",
        ))
    }
}
